import React, { useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { SourceConfig } from '../../types/skill';
import { ToastType } from '../ui/Toast';
import * as skillService from '../../services/skillService';
import { useQueryClient } from '@tanstack/react-query';

interface SourcesTableProps {
  sources: SourceConfig[];
  onRemove: (id: string) => void;
  showToast: (message: string, type: ToastType, duration?: number) => void;
}

export const SourcesTable: React.FC<SourcesTableProps> = ({ sources, onRemove, showToast }) => {
  const { darkMode } = useTheme();
  const queryClient = useQueryClient();
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());

  const addSyncing = useCallback((id: string) => {
    setSyncingIds(prev => new Set(prev).add(id));
  }, []);

  const removeSyncing = useCallback((id: string) => {
    setSyncingIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  if (sources.length === 0) {
    return (
      <div className={`text-center py-12 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No sources configured
      </div>
    );
  }

  const handleSync = async (sourceId: string) => {
    addSyncing(sourceId);
    try {
      const report = await skillService.syncSource(sourceId);
      const added = report?.added?.length ?? 0;
      const updated = report?.updated?.length ?? 0;
      const removed = report?.removed?.length ?? 0;
      const parts: string[] = [];
      if (added > 0) parts.push(`+${added} added`);
      if (updated > 0) parts.push(`~${updated} updated`);
      if (removed > 0) parts.push(`-${removed} removed`);
      const summary = parts.length > 0 ? parts.join(', ') : 'no changes';
      showToast(`Synced ${sourceId}: ${summary}`, 'success');
      queryClient.invalidateQueries(['skillSources']);
      queryClient.invalidateQueries(['skills']);
    } catch (e: any) {
      showToast(
        `Sync failed for ${sourceId}: ${e?.response?.data?.detail ?? e?.message ?? 'Unknown error'}`,
        'error',
        8000,
      );
    } finally {
      removeSyncing(sourceId);
    }
  };

  return (
    <div className={`overflow-x-auto rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <table className={`w-full text-sm ${darkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`}>
        <thead className={darkMode ? 'bg-gray-900' : 'bg-gray-50'}>
          <tr>
            {['Name', 'Trust', 'Last Sync', 'Skills', 'Actions'].map((h) => (
              <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {sources.map((src) => {
            const isSyncing = syncingIds.has(src.id);
            return (
              <tr key={src.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                <td className="px-4 py-3">
                  <div className="font-medium">{src.name}</div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{src.repo_url}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    src.trust_tier === 'auto_trusted'
                      ? darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700'
                      : darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700'
                  }`}>
                    {src.trust_tier}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">
                  {src.last_synced ? new Date(src.last_synced).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-4 py-3 text-xs">{src.skills_count}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSync(src.id)}
                      disabled={isSyncing}
                      className={`text-xs px-2 py-1 rounded transition-colors ${
                        isSyncing ? 'opacity-60 cursor-not-allowed' : ''
                      } ${darkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                    >
                      {isSyncing ? 'Syncing...' : 'Sync'}
                    </button>
                    <button
                      onClick={() => onRemove(src.id)}
                      disabled={isSyncing}
                      className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-red-900/40 hover:bg-red-800 text-red-300' : 'bg-red-50 hover:bg-red-100 text-red-600'}`}
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
